import { lazy } from 'react'
import { Navigate, Route } from 'react-router-dom'
import { ADMIN_CAPABILITIES } from './capabilities'
import { ADMIN_NAVIGATION_ITEMS } from './navigation'
import AdminCapabilityRoute from '@components/admin-shell/AdminCapabilityRoute'

const PAGE_COMPONENTS = {
  home: lazy(() => import('@pages/admin/AdminOverviewPage')),
  analytics: lazy(() => import('@pages/admin/AdminAnalyticsPage')),
  'analytics-legacy': lazy(
    () =>
      import(
        '@pages/admin/AdminLegacyAnalyticsPage'
      )
  ),
  users: lazy(() => import('@pages/admin/AdminUsersPage')),
  subscriptions: lazy(() => import('@pages/admin/AdminSubscriptionsPage')),
  'content-posts': lazy(
    () => import('@pages/admin/AdminContentPostsPage')
  ),
  'content-comments': lazy(
    () => import('@pages/admin/AdminContentCommentsPage')
  ),
  'content-post-editor': lazy(
    () => import('@pages/admin/AdminContentPostEditorPage')
  ),
  'content-ai-queue': lazy(
    () => import('@pages/admin/AdminContentAIQueuePage')
  ),
  'content-schedules': lazy(
    () => import('@pages/admin/AdminContentSchedulesPage')
  ),
  'content-analytics': lazy(
    () => import('@pages/admin/AdminContentAnalyticsPage')
  ),
  'content-settings': lazy(
    () => import('@pages/admin/AdminContentSettingsPage')
  ),
  'ai-signals': lazy(() => import('@pages/admin/AdminAISignalsPage')),
  'ai-agents': lazy(() => import('@pages/admin/AdminAIAgentsPage')),
  'ai-jobs': lazy(() => import('@pages/admin/AdminAIJobsPage')),
  'ai-usage': lazy(() => import('@pages/admin/AIUsageAdminPage')),
  'ai-providers': lazy(
    () => import('@pages/admin/AdminProviderSettingsPage')
  ),
  'operations-control': lazy(
    () => import('@pages/admin/AdminOperationsPage')
  ),
  'operations-jobs': lazy(
    () => import('@pages/admin/AdminJobsQueuesPage')
  ),
  'operations-integrations': lazy(
    () => import('@pages/admin/AdminIntegrationsPage')
  ),
  'operations-rollover': lazy(
    () => import('@pages/admin/AdminContractRolloverPage')
  ),
  'operations-mcp': lazy(() => import('@pages/admin/AdminMCPRuntimePage')),
  'operations-health': lazy(
    () => import('@pages/admin/AdminSystemHealthPage')
  ),
  'operations-observability': lazy(
    () =>
      import(
        '@pages/admin/AdminObservabilityPage'
      )
  ),
  access: lazy(() => import('@pages/admin/AdminAccessPage')),
  audit: lazy(() => import('@pages/admin/AdminAuditPage')),
  security: lazy(() => import('@pages/admin/AdminSecurityPage'))
}

export const ADMIN_CANONICAL_ROUTES = Object.freeze(
  ADMIN_NAVIGATION_ITEMS.map(item => ({
    id: item.id,
    path: item.path,
    capability: item.capability
  }))
)

export const ADMIN_SUPPLEMENTAL_ROUTES = Object.freeze([
  {
    id: 'content-post-create',
    path: '/admin/content/posts/new',
    capability: ADMIN_CAPABILITIES.CONTENT_MANAGE
  },
  {
    id: 'content-post-edit',
    path: '/admin/content/posts/:postId/edit',
    capability: ADMIN_CAPABILITIES.CONTENT_MANAGE
  }
])

function getRelativePath(path) {
  return path === '/admin' ? null : path.replace(/^\/admin\//, '')
}

export function getAdminRouteElements() {
  const navigationRoutes = ADMIN_NAVIGATION_ITEMS.map(item => {
    const Page = PAGE_COMPONENTS[item.page]
    if (!Page) {
      throw new Error(`No admin page registered for ${item.id}`)
    }

    const element = (
      <AdminCapabilityRoute capability={item.capability}>
        <Page navigationItem={item} />
      </AdminCapabilityRoute>
    )

    if (item.path === '/admin') {
      return <Route key={item.id} index element={element} />
    }

    return (
      <Route
        key={item.id}
        path={getRelativePath(item.path)}
        element={element}
      />
    )
  })

  const EditorPage = PAGE_COMPONENTS['content-post-editor']
  const editorRoutes = ADMIN_SUPPLEMENTAL_ROUTES.map(route => (
    <Route
      key={route.id}
      path={getRelativePath(route.path)}
      element={
        <AdminCapabilityRoute capability={route.capability}>
          <EditorPage />
        </AdminCapabilityRoute>
      }
    />
  ))

  return [...navigationRoutes, ...editorRoutes]
}

export function getAdminCompatibilityRouteElements() {
  return [
    <Route
      key="legacy-admin-event-explorer"
      path="analytics/events"
      element={<Navigate to="/admin/analytics?view=events" replace />}
    />,
    <Route
      key="legacy-admin-overview"
      path="overview"
      element={<Navigate to="/admin" replace />}
    />,
    <Route
      key="legacy-admin-content"
      path="content"
      element={<Navigate to="/admin/content/posts" replace />}
    />,
    <Route
      key="legacy-admin-ai"
      path="ai"
      element={<Navigate to="/admin/ai/market-monitor" replace />}
    />,
    <Route
      key="legacy-admin-ai-signals"
      path="ai/signals"
      element={<Navigate to="/admin/ai/market-monitor" replace />}
    />,
    <Route
      key="legacy-admin-monitoring"
      path="monitoring"
      element={<Navigate to="/admin/operations/health" replace />}
    />,
    <Route
      key="legacy-admin-ai-usage"
      path="ai-usage"
      element={<Navigate to="/admin/ai/usage" replace />}
    />
  ]
}
